// Wire the UX modules (shortcuts, notifications, wake lock, SW) on top of
// the original app / tweaks / audio / buddha modules that were migrated
// to TS in this iteration.
import "./styles/zen.css";
import "./types";

// Import modules that export values
import { ZenAudio } from "./audio";
import { Buddha } from "./buddha";
import * as notify from "./notify";
import * as wake from "./wakelock";
import { ZenAPI } from "./api";

// Set window globals before importing side-effect modules.
(window as unknown as { ZenAudio: typeof ZenAudio }).ZenAudio = ZenAudio;
(window as unknown as { Buddha: typeof Buddha }).Buddha = Buddha;
(window as unknown as { ZenNotify: typeof notify }).ZenNotify = notify;
(window as unknown as { ZenWake: typeof wake }).ZenWake = wake;
(window as unknown as { ZenAPI: typeof ZenAPI }).ZenAPI = ZenAPI;

// Fire event to signal that globals are ready (app.ts listens for this).
window.dispatchEvent(new CustomEvent("bt:appReady"));

// Dynamically import side-effect modules AFTER globals are set.
// This ensures app.ts can access window.Buddha when init() is called.
void Promise.all([
  import("./app"),
  import("./tweaks"),
  import("./install-prompt"),
  import("./oracle"),
  import("./v10"),
]).then(async () => {
  // Import remaining modules dynamically to ensure proper loading order
  const [{ registerServiceWorker }, { bindShortcuts, SHORTCUT_HELP_HTML }] = await Promise.all([
    import("./sw-register"),
    import("./shortcuts"),
  ]);

  // Now run the main setup with all modules loaded
  setupMain(registerServiceWorker, bindShortcuts, SHORTCUT_HELP_HTML, notify, wake);
});

function showToast(msg: string, ms = 3200): void {
  const el = document.getElementById("toast");
  if (!el) return;
  el.textContent = msg;
  el.classList.add("show");
  setTimeout(() => el.classList.remove("show"), ms);
}

function showHelpModal(SHORTCUT_HELP_HTML: string): void {
  // Lightweight modal — re-uses the existing scrim/card pattern from index.html.
  let scrim = document.getElementById("helpScrim");
  if (!scrim) {
    scrim = document.createElement("div");
    scrim.id = "helpScrim";
    scrim.className = "modal-scrim";
    scrim.addEventListener("click", closeHelp);
    const card = document.createElement("div");
    card.className = "modal-card";
    card.id = "helpCard";
    card.innerHTML = `
      <h2>快捷鍵 <button class="stats-close" id="helpClose" type="button" aria-label="關閉">✕</button></h2>
      <p class="modal-sub">一鍵出入，雙手不必離開鍵盤。</p>
      ${SHORTCUT_HELP_HTML}
    `;
    document.body.appendChild(scrim);
    document.body.appendChild(card);
    document.getElementById("helpClose")?.addEventListener("click", closeHelp);
  }
  scrim.classList.add("open");
  document.getElementById("helpCard")?.classList.add("open");
}

function closeHelp(): void {
  document.getElementById("helpScrim")?.classList.remove("open");
  document.getElementById("helpCard")?.classList.remove("open");
}

function toggleStats(): void {
  const open = document.getElementById("statsPanel")?.classList.toggle("open");
  document.getElementById("statsScrim")?.classList.toggle("open", !!open);
}

function toggleTweaks(): void {
  const panel = document.getElementById("tw-panel");
  if (panel?.classList.contains("open")) {
    panel.classList.remove("open");
    document.getElementById("tw-scrim")?.classList.remove("open");
  } else {
    document.getElementById("tweaksBtn")?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
  }
}

function toggleMusic(): void {
  document.getElementById("playBtn")?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
}

function closeAnyPanel(): void {
  // Esc behavior: close whichever is open, then maybe the login modal.
  if (document.getElementById("helpCard")?.classList.contains("open")) { closeHelp(); return; }
  if (document.getElementById("tw-panel")?.classList.contains("open")) { toggleTweaks(); return; }
  if (document.getElementById("oraclePanel")?.classList.contains("open")) {
    document.getElementById("oracleClose")?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    return;
  }
  if (document.getElementById("statsPanel")?.classList.contains("open")) { toggleStats(); return; }
  if (document.getElementById("loginModal")?.classList.contains("open")) {
    document.getElementById("loginClose")?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
  }
}

function dispatchAppEvent(name: string, detail?: unknown): void {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

type RegisterServiceWorker = typeof import("./sw-register").registerServiceWorker;
type BindShortcuts = typeof import("./shortcuts").bindShortcuts;
type SHelpHTML = string;
type NotifyModule = typeof notify;
type WakeModule = typeof wake;

function setupMain(
  registerServiceWorker: RegisterServiceWorker,
  bindShortcuts: BindShortcuts,
  SHORTCUT_HELP_HTML: SHelpHTML,
  notify: NotifyModule,
  wake: WakeModule,
): void {
  // Wire keyboard shortcuts. app.ts owns the timer; we just dispatch the same
  // DOM events the buttons would.
  bindShortcuts({
    onToggle: () => document.getElementById("startBtn")?.dispatchEvent(new MouseEvent("click", { bubbles: true })),
    onReset: () => document.getElementById("resetBtn")?.dispatchEvent(new MouseEvent("click", { bubbles: true })),
    onMode: (m) => {
      const tab = document.querySelector<HTMLElement>(`.mode-tab[data-mode="${m}"]`);
      tab?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    },
    onStats: toggleStats,
    onTweaks: toggleTweaks,
    onMusic: toggleMusic,
    onClose: closeAnyPanel,
    onHelp: () => showHelpModal(SHORTCUT_HELP_HTML),
  });

  // Wire timerStart/Pause/Complete events into notify + wake lock + notify.
  window.addEventListener("bt:timerStart", (e) => {
    const detail = (e as CustomEvent<{ mode: string }>).detail;
    if (detail?.mode === "focus") void wake.setEnabled(true);
  });
  window.addEventListener("bt:timerPause", () => void wake.setEnabled(false));
  window.addEventListener("bt:timerReset", () => void wake.setEnabled(false));
  window.addEventListener("bt:timerComplete", (e) => {
    const detail = (e as CustomEvent<{ mode: string }>).detail;
    const isFocus = detail?.mode === "focus";
    if (isFocus) void wake.setEnabled(false);
    if (window.__btUserSettings?.notifyOnComplete) {
      notify.show({
        title: isFocus ? "功德圓滿" : "休息圓滿，回到當下",
        body: isFocus ? "一念專注 · 功德 +1" : "願您自在",
        tag: `bt-complete-${detail?.mode ?? "unknown"}`,
      });
    }
  });

  // Service worker: prompt the user to reload when a new version is available.
  const sw = registerServiceWorker({
    immediate: true,
    onNeedRefresh: () => {
      showToast("有新版本 · 點此重啟", 6000);
      // Click on the toast = reload to new version.
      const el = document.getElementById("toast");
      if (!el) return;
      el.style.cursor = "pointer";
      el.onclick = () => { void sw.update(true); };
    },
    onOfflineReady: () => showToast("已可離線使用", 3000),
  });
  // Expose for hot reload / debugging.
  (window as unknown as { __btSW: typeof sw }).__btSW = sw;

  // Settings → notify / wake lock cross-talk.
  let lastNotify = window.__btUserSettings?.notifyOnComplete;
  let lastKeepAwake = window.__btUserSettings?.keepAwake;
  window.addEventListener("storage", (ev) => {
    if (ev.key !== "bt_tweaks_v1" || !ev.newValue) return;
    try {
      const s = JSON.parse(ev.newValue) as { notifyOnComplete?: boolean; keepAwake?: boolean };
      if (typeof s.notifyOnComplete === "boolean" && s.notifyOnComplete !== lastNotify) {
        lastNotify = s.notifyOnComplete;
        if (s.notifyOnComplete) {
          if (notify.notifyStatus() === "default") {
            showToast("點此開啟「完成時通知我」", 6000);
            const el = document.getElementById("toast");
            if (el) el.onclick = () => { void notify.requestPermission().then(() => { el.onclick = null; }); };
          }
        }
      }
      if (typeof s.keepAwake === "boolean" && s.keepAwake !== lastKeepAwake) {
        lastKeepAwake = s.keepAwake;
        void wake.setEnabled(s.keepAwake);
      }
    } catch { /* */ }
  });
}
